const DEFS = {
    'billing.read_invoice': {
        kind: 'billing.read_invoice',
        mutation: false,
        mcpToolGroup: 'billing',
        mcpToolName: 'read-invoice',
    },
    // Código de PAGAMENTO de uma fatura/cobrança (PIX copia-e-cola / linha digitável / código
    // de barras do boleto). Leitura pura (consulta `consultar-cobranca-boleto`). Distinto de
    // `read_invoice` (informação/2ª via PDF) — aqui é o código pagável inline.
    'billing.payment_code': {
        kind: 'billing.payment_code',
        mutation: false,
        mcpToolGroup: 'billing',
        mcpToolName: 'payment-code',
    },
    'clinical.read_exam_status': {
        kind: 'clinical.read_exam_status',
        mutation: false,
        mcpToolGroup: 'clinical',
        mcpToolName: 'read-exam-status',
    },
    'legal.read_case_status': {
        kind: 'legal.read_case_status',
        mutation: false,
        mcpToolGroup: 'legal',
        mcpToolName: 'read-case-status',
    },
    'service.appointment_status': {
        kind: 'service.appointment_status',
        mutation: false,
        mcpToolGroup: 'service',
        mcpToolName: 'appointment-status',
    },
    'commercial.track_order': {
        kind: 'commercial.track_order',
        mutation: false,
        mcpToolGroup: 'commercial',
        mcpToolName: 'track-order',
    },
    // Diagnóstico de conexão ao vivo (telecom — "internet caiu / tem sinal?"). Leitura.
    'service.connection_status': {
        kind: 'service.connection_status',
        mutation: false,
        mcpToolGroup: 'service',
        mcpToolName: 'connection-status',
    },
    // Status de um chamado/ordem de serviço (OS)/protocolo já aberto ("foi resolvido?"). Leitura.
    'service.ticket_status': {
        kind: 'service.ticket_status',
        mutation: false,
        mcpToolGroup: 'service',
        mcpToolName: 'ticket-status',
    },
    // Datas/janelas disponíveis para agendar uma visita (read; slots-first da Etapa 2).
    'service.list_visit_slots': {
        kind: 'service.list_visit_slots',
        mutation: false,
        mcpToolGroup: 'service',
        mcpToolName: 'list-visit-slots',
    },
    // Condições CONTRATADAS do contrato em foco (nome do plano/serviço, valor recorrente,
    // fidelidade). Leitura. ★ NÃO é alcançável pelo conductor: nenhuma `IntentAction` mapeia para
    // ele (o kind vem da intenção, nunca do nome livre do modelo). Quem lê é a PLATAFORMA, no
    // contexto do turno (Item 526 — a IA via o valor da fatura e nunca o contratado).
    'commercial.read_plan': {
        kind: 'commercial.read_plan',
        mutation: false,
        mcpToolGroup: 'commercial',
        mcpToolName: 'read-plan',
    },
    // Mutações — cabeadas no gate/executor; tool concreta na fatia 2.
    'service.schedule_visit': {
        kind: 'service.schedule_visit',
        mutation: true,
        mcpToolGroup: 'service',
        mcpToolName: 'schedule-visit',
    },
    'cadastral.update_address': {
        kind: 'cadastral.update_address',
        mutation: true,
        mcpToolGroup: 'cadastral',
        mcpToolName: 'update-address',
    },
};
/** Default por ação (telecom/varejo/cobrança herdam). `null` = ação sem tool. */
const TOOL_KIND_DEFAULT = {
    public_info: null,
    account_context: null,
    sensitive_data_read: 'billing.read_invoice',
    billing_payment_code_read: 'billing.payment_code',
    service_status_read: 'service.appointment_status',
    service_connection_read: 'service.connection_status',
    service_ticket_read: 'service.ticket_status',
    cadastral_change: 'cadastral.update_address',
    service_slots_query: 'service.list_visit_slots',
    service_action: 'service.schedule_visit',
    // AC3 — confirmar visita é escrita NATIVA do Lume (`Appointment.status='confirmed'`),
    // NÃO uma tool MCP → sem ToolKind. Se o modelo emitir call_tool com esta ação,
    // `toolKindForAction` retorna null → handoff (fail-safe): a confirmação acontece no
    // caminho de `reply` (registerAppointmentConfirmation), nunca via executeToolCall.
    appointment_confirm: null,
    // Agenda nativa (Frente B Bloco II) — escrita/leitura NATIVA do Lume
    // (NativeSchedulingService), NÃO tool MCP → sem ToolKind. O conductor ramifica por
    // `ic.action` (conjunto AGENDA_NATIVE_ACTIONS) ANTES da resolução MCP; nunca passa por aqui.
    agenda_availability_query: null,
    agenda_book: null,
    agenda_reschedule: null,
    agenda_cancel: null,
    // Frente B Bloco IV — entrar na fila é escrita NATIVA (WaitlistService), NÃO tool MCP
    // → sem ToolKind (roteada por AGENDA_NATIVE_ACTIONS antes da resolução MCP).
    agenda_join_waitlist: null,
    out_of_scope: null,
};
/** Override esparso por vertical (design-for-platform). */
// commercial/varejo/cobrança herdam o default telecom (ausência intencional — sem tool própria).
const TOOL_KIND_BY_VERTICAL = {
    healthcare: { sensitive_data_read: 'clinical.read_exam_status' },
    legal: { sensitive_data_read: 'legal.read_case_status' },
    commercial: { service_status_read: 'commercial.track_order' },
};
/** Resolve o ToolKind abstrato que serve uma ação no vertical (override esparso → default). */
export function toolKindForAction(action, vertical) {
    if (vertical != null && vertical in TOOL_KIND_BY_VERTICAL) {
        const override = TOOL_KIND_BY_VERTICAL[vertical];
        if (action in override)
            return override[action] ?? null;
    }
    return TOOL_KIND_DEFAULT[action];
}
/** Def das coordenadas MCP de um ToolKind (ou `undefined` se desconhecido). */
export function toolKindDef(kind) {
    return DEFS[kind];
}
/** `true` se o ToolKind muta estado externo. Kind desconhecido → `false` (fail-safe: não trata como write). */
export function isMutationToolKind(kind) {
    return DEFS[kind]?.mutation ?? false;
}
/**
 * Mapa-reverso autoritativo `${mcpToolGroup}.${mcpToolName}` → `mutation`, derivado
 * do catálogo. Chaveado por group+name (não bare) para desambiguar colisões de nome.
 */
const MCP_TOOL_MUTATION_BY_GROUP_NAME = Object.fromEntries(Object.values(DEFS).map((def) => [`${def.mcpToolGroup}.${def.mcpToolName}`, def.mutation]));
/**
 * `true` se a tool MCP concreta (identificada por `mcpToolGroup` + `mcpToolName`) muta
 * estado externo. Fonte da verdade da fronteira de segurança do `MCPClientService.callTool`
 * (Etapa 2 PR0) — zero-trust no caller. Tool fora do catálogo → `false` (fail-safe: não
 * quebra reads não-catalogadas como `find-bond`; writes desconhecidas não são invocáveis
 * pelo conductor de qualquer forma, pois exigem ToolKind).
 */
export function isMcpToolMutation(mcpToolGroup, mcpToolName) {
    return MCP_TOOL_MUTATION_BY_GROUP_NAME[`${mcpToolGroup}.${mcpToolName}`] ?? false;
}
/** Type guard — a string emitida pelo conductor é um ToolKind reconhecido do catálogo? */
export function isToolKind(value) {
    return value in DEFS;
}
//# sourceMappingURL=tool-catalog.js.map